import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.js';
import LocationDetailModal from './LocationDetailModal';

// Fix marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const MapView = forwardRef(({ 
  locations, 
  center = [25.3862, 97.3945], 
  zoom = 13,
  waterLevel = 0,
  setWaterLevel = null,
  onLocationSelect = null,
  onMapClick = null,
  basemap = 'osm',
  showGPS = false,
  userLocation = null,
  onLocationUpdate = null,
  onShowRoute = null
}, ref) => {

  console.log('📍 MapView - showGPS:', showGPS);
  console.log('📍 MapView - userLocation:', userLocation);
  console.log('📍 MapView - basemap:', basemap);

  // Refs
  const mapRef = useRef(null);
  const markerClusterRef = useRef(null);
  const floodLayerRef = useRef(null);
  const markersRef = useRef({});
  const userMarkerRef = useRef(null);
  const clickMarkerRef = useRef(null);
  const tileLayerRef = useRef(null);
  const routingControlRef = useRef(null);
  const userLocationRef = useRef(null);
  const routeDestMarkerRef = useRef(null);

  // State for Detail Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLocationDetail, setSelectedLocationDetail] = useState(null);
  const [citizenReports, setCitizenReports] = useState([]);

  // ✅ Fetch Citizen Reports
  useEffect(() => {
    const fetchReports = async () => {
        try {
            const response = await fetch('/api/reports');
            const data = await response.json();
            if (data.success) {
                setCitizenReports(data.data || []);
                console.log('📢 Citizen reports loaded:', data.data?.length || 0);
            }
        } catch (error) {
            console.error('Error fetching reports:', error);
        }
    };
    fetchReports();
  }, []);

  // ✅ Citizen Report Markers
  useEffect(() => {
    if (!mapRef.current) return;

    citizenReports.forEach(report => {
        if (report.status === 'pending' || report.status === 'in_progress') {
            const reportIcon = L.divIcon({
                html: `<div style="background:#ef4444;border-radius:50%;width:20px;height:20px;border:3px solid white;box-shadow:0 2px 10px rgba(239,68,68,0.5);display:flex;align-items:center;justify-content:center;font-size:10px;color:white;font-weight:bold;">!</div>`,
                className: 'report-marker',
                iconSize: [20, 20],
                iconAnchor: [10, 10]
            });
            
            const reportTypeMap = {
                road: '🚧 လမ်းတံတားပျက်စီးမှု',
                trash: '🗑️ အမှိုက်ပုံပြဿနာ',
                electric: '💡 မီးလင်းရေးလိုအပ်ချက်',
                water: '💧 ရေပေးဝေရေးပြဿနာ',
                drainage: '🚿 ရေနုတ်မြောင်းပိတ်ဆို့ခြင်း',
                noise: '🔊 ဆူညံသံညစ်ညမ်းမှု',
                fire: '🔥 မီးလောင်မှု',
                other: '📝 အခြား'
            };
            
            const marker = L.marker([report.lat, report.lng], {
                icon: reportIcon
            }).bindPopup(`
                <div style="min-width:220px;padding:4px 0;">
                    <h4 style="margin:0 0 8px 0;color:#ef4444;">📢 ${reportTypeMap[report.report_type] || report.report_type}</h4>
                    <p style="margin:0 0 8px 0;font-size:13px;color:#4b5563;">${report.description || 'အကြောင်းအရာမရှိပါ'}</p>
                    <div style="font-size:12px;color:#6b7280;margin-bottom:6px;">
                        📅 ${new Date(report.created_at).toLocaleDateString('my-MM')}
                        ${report.phone ? ` | 📞 ${report.phone}` : ''}
                    </div>
                    <span style="background:${report.status === 'pending' ? '#f59e0b' : '#3b82f6'};padding:2px 10px;border-radius:12px;font-size:11px;color:white;display:inline-block;">
                        ${report.status === 'pending' ? '⏳ ဆိုင်းငံ့' : '🔵 လုပ်ဆောင်ဆဲ'}
                    </span>
                    ${report.location_name ? `<div style="font-size:11px;color:#94a3b8;margin-top:4px;">📍 ${report.location_name}</div>` : ''}
                </div>
            `);
            
            marker.addTo(mapRef.current);
        }
    });
  }, [citizenReports]);

  // Basemap URLs
  const basemapUrls = {
    osm: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    dark: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    wms: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
  };

  const basemapAttribution = {
    osm: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
    dark: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
    satellite: '&copy; <a href="https://www.esri.com">Esri</a>',
    wms: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
  };

  // ============================================================
  // ✅ TRANSLATE INSTRUCTION FUNCTION
  // ============================================================
  const translateInstruction = (text) => {
    const translations = {
      'Head': 'ဦးတည်သွားပါ',
      'Continue': 'ဆက်သွားပါ',
      'Turn left': 'ဘယ်ကွေ့ပါ',
      'Turn right': 'ညာကွေ့ပါ',
      'Turn sharp left': 'ဘယ်ဘက်ပြင်းထန်ကွေ့ပါ',
      'Turn sharp right': 'ညာဘက်ပြင်းထန်ကွေ့ပါ',
      'Slight left': 'ဘယ်ဘက်အနည်းငယ်ကွေ့ပါ',
      'Slight right': 'ညာဘက်အနည်းငယ်ကွေ့ပါ',
      'Straight': 'ဖြောင့်တန်းသွားပါ',
      'Enter roundabout': 'ပတ်လမ်းဝင်ပါ',
      'Exit roundabout': 'ပတ်လမ်းမှထွက်ပါ',
      'Turn around': 'ပြန်လှည့်ပါ',
      'Destination': 'ဦးတည်ရာသို့ရောက်ပါပြီ',
      'Depart': 'စတင်ပါ',
      'at the fork': 'လမ်းခွဲတွင်ကွေ့ပါ',
      'northeast': 'အရှေ့မြောက်',
      'northwest': 'အနောက်မြောက်',
      'southeast': 'အရှေ့တောင်',
      'southwest': 'အနောက်တောင်',
      'north': 'မြောက်',
      'south': 'တောင်',
      'east': 'အရှေ့',
      'west': 'အနောက်',
      'right': 'ညာဘက်',
      'left': 'ဘယ်ဘက်',
      'Road': 'လမ်း',
      'Waypoint': 'လမ်းမှတ်တိုင်'
    };

    let translated = text;
    for (const [eng, mya] of Object.entries(translations)) {
      if (translated.includes(eng)) {
        translated = translated.replace(eng, mya);
      }
    }
    return translated;
  };

  // ============================================================
  // ✅ CREATE POPUP CONTENT FUNCTION
  // ============================================================
  const createPopupContent = (loc) => {
    return `
      <div style="min-width:220px;padding:4px 0;">
        <div style="font-size:16px;font-weight:bold;color:#3b82f6;margin-bottom:6px;">
          ${loc.name_mm || 'အမည်မရှိ'}
        </div>
        <div style="margin:6px 0;font-size:13px;color:#4b5563;">
          🏷️ ${loc.category || 'အမျိုးအစားမရှိ'}<br/>
          📂 ${loc.subcategory || ''}<br/>
          📞 ${loc.phone || 'မရှိ'}<br/>
          ⭐ ${loc.rating || 'N/A'}/5
        </div>
        <div style="display:flex;gap:6px;margin-top:10px;">
          <button 
            id="detail-btn-${loc.id}" 
            class="popup-detail-btn"
            style="flex:1;background:#3b82f6;border:none;padding:8px 12px;border-radius:6px;color:white;cursor:pointer;font-size:13px;font-weight:500;"
          >
            📋 အသေးစိတ်
          </button>
          <button 
            id="route-btn-${loc.id}" 
            class="popup-route-btn"
            style="flex:1;background:#22c55e;border:none;padding:8px 12px;border-radius:6px;color:white;cursor:pointer;font-size:13px;font-weight:500;"
          >
            🗺️ လမ်းပြရန်
          </button>
        </div>
      </div>
    `;
  };

  // ============================================================
  // ✅ CLEAR ROUTE FUNCTION
  // ============================================================
  const clearRoute = () => {
    if (routingControlRef.current) {
      try {
        mapRef.current.removeControl(routingControlRef.current);
      } catch (e) {
        console.log('Routing control already removed');
      }
      routingControlRef.current = null;
    }

    if (routeDestMarkerRef.current) {
      try {
        mapRef.current.removeLayer(routeDestMarkerRef.current);
      } catch (e) {
        console.log('Destination marker already removed');
      }
      routeDestMarkerRef.current = null;
    }

    const routeBtn = document.querySelector('.leaflet-control-route');
    if (routeBtn) {
      routeBtn.innerHTML = '🗺️ လမ်းကြောင်းပြရန်';
      routeBtn.style.backgroundColor = '#1e293b';
    }

    console.log('🗑️ Route cleared');
  };

  // ============================================================
  // ✅ SHOW ROUTE FUNCTION - ပြင်ဆင်ပြီး
  // ============================================================
  const showRoute = (destination) => {
    console.log('📍 showRoute called with:', destination);
    
    if (!mapRef.current) {
      console.warn('⚠️ Map not initialized');
      return;
    }

    // Clear existing route
    clearRoute();

    // ✅ Get start location
    let start = userLocationRef.current;
    
    // If no GPS location, use userLocation prop
    if (!start && userLocation) {
      start = userLocation;
      console.log('📍 Using userLocation prop as start:', start);
    }
    
    // If still no start, use default or show alert
    if (!start) {
      console.warn('⚠️ No GPS location, using default');
      start = { lat: 25.3833, lng: 97.4000 };
      
      // If still no location, show error
      if (!start) {
        alert('ကျေးဇူးပြု၍ GPS Location ခွင့်ပြုပေးပါ သို့မဟုတ် မြေပုံပေါ်တွင် နေရာရွေးပါ');
        return;
      }
    }

    // ✅ Get destination
    if (!destination || !destination.lat || !destination.lng) {
      console.warn('⚠️ Invalid destination:', destination);
      alert('ဦးတည်ရာနေရာ မှားယွင်းနေပါသည်။');
      return;
    }

    const end = {
      lat: parseFloat(destination.lat),
      lng: parseFloat(destination.lng)
    };

    console.log("🚗 Route Start:", start);
    console.log("🎯 Route End:", end);

    const formatter = new L.Routing.Formatter({
      units: 'metric',
      language: 'en',
      distanceTemplate: '{{value}} km',
      timeTemplate: '{{value}} min',
      template: '{{distance}}, {{time}}'
    });

    // ✅ Create routing control
    routingControlRef.current = L.Routing.control({
      waypoints: [
        L.latLng(start.lat, start.lng),
        L.latLng(end.lat, end.lng)
      ],
router: L.Routing.osrmv1({
  serviceUrl: 'https://www.myitkyinamap.com/osrm/route/v1',  // ✅ ဒါကိုသုံးပါ
  profile: 'driving'
}),
      routeWhileDragging: false,
      addWaypoints: false,
      draggableWaypoints: false,
      fitSelectedRoutes: true,
      showAlternatives: false,
      collapsible: true,
      lineOptions: {
        styles: [{
          color: '#2563eb',
          weight: 6,
          opacity: 0.9
        }]
      },
      createMarker: function() {
        return null;
      },
      formatter: formatter
    }).addTo(mapRef.current);

    // ✅ Route Found
    routingControlRef.current.on('routesfound', function(e) {
      if (!mapRef.current) {
        console.warn("Map removed");
        return;
      }

      console.log("✅ Route Found", e.routes);
      const route = e.routes[0];
      const distance = (route.summary.totalDistance / 1000).toFixed(2);
      const duration = Math.round(route.summary.totalTime / 60);

      const distanceText = distance + ' ကီလိုမီတာ';
      const durationText = duration + ' မိနစ်';

      let instructionsHTML = "";
      if (route.instructions && route.instructions.length > 0) {
        route.instructions.forEach((step, index) => {
          const myanmarText = translateInstruction(step.text);
          const stepDistance = step.distance ? (step.distance / 1000).toFixed(1) : '0';
          instructionsHTML += `
            <div style="margin-bottom:5px; font-size:13px;">
              ${index + 1}. ${myanmarText} (${stepDistance} km)
            </div>
          `;
        });
      } else {
        instructionsHTML = "<div>လမ်းညွှန်ချက် မရရှိနိုင်ပါ</div>";
      }

      // ✅ Add destination marker
      if (routeDestMarkerRef.current) {
        mapRef.current.removeLayer(routeDestMarkerRef.current);
      }

      const destIcon = L.divIcon({
        html: '<div style="background:#22c55e;border-radius:50%;width:16px;height:16px;border:3px solid white;box-shadow:0 2px 10px rgba(34,197,94,0.5);"></div>',
        className: 'dest-marker',
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });

      routeDestMarkerRef.current = L.marker([end.lat, end.lng], {
        icon: destIcon
      }).addTo(mapRef.current)
        .bindPopup(`
          <div style="min-width:280px; max-height:400px; overflow-y:auto; padding:4px 0;">
            <h4 style="margin:0 0 8px 0; color:#2563eb;">🚗 လမ်းကြောင်းအချက်အလက်</h4>
            
            <div style="display:flex; gap:15px; margin-bottom:10px;">
              <div>
                <span style="font-weight:bold;">📏 အကွာအဝေး</span><br/>
                <span style="font-size:16px; color:#2563eb;">${distanceText}</span>
              </div>
              <div>
                <span style="font-weight:bold;">⏱️ ကြာချိန်</span><br/>
                <span style="font-size:16px; color:#2563eb;">${durationText}</span>
              </div>
            </div>
            
            <hr style="border-color:#e5e7eb; margin:8px 0;">
            
            <div style="font-weight:600; margin-bottom:5px;">🔄 လမ်းညွှန်ချက်များ:</div>
            <div style="font-size:13px;">
              ${instructionsHTML}
            </div>
            
            <hr style="border-color:#e5e7eb; margin:8px 0;">
            
            <button
              id="clear-route-btn"
              style="width:100%; background:#ef4444; color:white; border:none; padding:10px; border-radius:6px; cursor:pointer; font-size:14px; font-weight:600;"
            >
              ❌ လမ်းကြောင်းရှင်းရန်
            </button>
          </div>
        `);

      routeDestMarkerRef.current.openPopup();

      setTimeout(() => {
        const btn = document.getElementById("clear-route-btn");
        if (btn) {
          btn.onclick = () => {
            clearRoute();
          };
        }
      }, 500);
    });

    // ✅ Route Error
    routingControlRef.current.on('routingerror', function(e) {
      console.error("❌ Routing Error:", e);
      alert("လမ်းကြောင်းတွက်ချက်၍ မရပါ။ ကျေးဇူးပြု၍ နောက်မှ ထပ်မံကြိုးစားပါ။");
    });
  };

  // ============================================================
  // ✅ useImperativeHandle - Expose functions
  // ============================================================
  useImperativeHandle(ref, () => ({
    showRoute: (destination) => {
      console.log('📍 MapView.showRoute called from ref with:', destination);
      showRoute(destination);
    },
    clearRoute: () => {
      clearRoute();
    },
    getMap: () => {
      return mapRef.current;
    }
  }));

  // ============================================================
  // ✅ HANDLE DETAIL CLOSE
  // ============================================================
  const handleDetailClose = () => {
    setShowDetailModal(false);
    setSelectedLocationDetail(null);
  };

  // ============================================================
  // ✅ HANDLE DETAIL ROUTE
  // ============================================================
  const handleDetailRoute = (lat, lng, name) => {
    setShowDetailModal(false);
    showRoute({
      lat: lat,
      lng: lng,
      name_mm: name
    });
  };

  // ============================================================
  // ✅ INITIALIZE MAP
  // ============================================================
  useEffect(() => {
    if (!mapRef.current) {
      console.log('🗺️ Creating map...');
      
      mapRef.current = L.map('map', {
        zoomControl: true,
        dragging: true,
        touchZoom: true,
        scrollWheelZoom: true,
        doubleClickZoom: true,
        boxZoom: true,
        keyboard: true,
        tap: true,
        attributionControl: false
      }).setView(center, zoom);
      
      const defaultUrl = basemapUrls.osm;
      tileLayerRef.current = L.tileLayer(defaultUrl, {
        attribution: ''
      }).addTo(mapRef.current);

      markerClusterRef.current = L.markerClusterGroup();
      mapRef.current.addLayer(markerClusterRef.current);

     // ✅ Map click handler - ပြင်ဆင်ပြီး
mapRef.current.on('click', function(e) {
  if (routingControlRef.current) {
    return;
  }
  
  // ✅ Get coordinates as numbers
  const lat = Number(e.latlng.lat);
  const lng = Number(e.latlng.lng);
  
  // ✅ Check if valid
  if (isNaN(lat) || isNaN(lng)) {
    console.warn('⚠️ Invalid coordinates:', e.latlng);
    return;
  }
  
  // Remove existing click marker
  if (clickMarkerRef.current) {
    mapRef.current.removeLayer(clickMarkerRef.current);
  }
  
  // ✅ Create click marker
  const clickIcon = L.divIcon({
    html: '<div style="background:#ef4444;border-radius:50%;width:16px;height:16px;border:3px solid white;box-shadow:0 2px 10px rgba(239,68,68,0.5);"></div>',
    className: 'click-marker',
    iconSize: [16, 16],
    iconAnchor: [8, 8]
  });
  
  // ✅ Format coordinates for display
  const latDisplay = lat.toFixed(6);
  const lngDisplay = lng.toFixed(6);
  
  clickMarkerRef.current = L.marker([lat, lng], {
    icon: clickIcon
  }).bindPopup(`
    <div style="min-width:200px;padding:4px 0;">
      <div style="font-weight:bold;color:#ef4444;margin-bottom:6px;">📍 ရွေးချယ်ထားသောနေရာ</div>
      <div style="font-size:13px;color:#4b5563;">
        <div>လတ္: ${latDisplay}</div>
        <div>လောင်း: ${lngDisplay}</div>
      </div>
      <div style="margin-top:8px;font-size:12px;color:#6b7280;">
        လမ်းကြောင်းပြရန် ခလုတ်ကို နှိပ်ပါ
      </div>
    </div>
  `).addTo(mapRef.current);
  
  // ✅ Call onMapClick with number values
  if (onMapClick) {
    onMapClick({
      lat: lat,   // ✅ Number
      lng: lng    // ✅ Number
    });
  }
});
      // ✅ Route Control Button
      const RouteControl = L.Control.extend({
        options: {
          position: 'topleft'
        },
        onAdd: function(map) {
          const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-route');
          
          container.innerHTML = '🗺️ လမ်းကြောင်းပြရန်';
          
          container.style.cssText = `
            background-color: #1e293b !important;
            color: #ffffff !important;
            padding: 8px 14px !important;
            border-radius: 4px !important;
            box-shadow: 0 2px 10px rgba(0,0,0,0.3) !important;
            cursor: pointer !important;
            font-size: 13px !important;
            font-weight: 600 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 6px !important;
            min-width: 130px !important;
            border: 1px solid #334155 !important;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
            z-index: 1000 !important;
            transition: all 0.2s ease !important;
          `;
          
          container.onmouseenter = function() {
            this.style.backgroundColor = '#334155';
            this.style.transform = 'scale(1.02)';
          };
          
          container.onmouseleave = function() {
            this.style.backgroundColor = '#1e293b';
            this.style.transform = 'scale(1)';
          };
          
          container.onclick = function(e) {
            e.stopPropagation();
            
            if (routingControlRef.current) {
              clearRoute();
              this.innerHTML = '🗺️ လမ်းကြောင်းပြရန်';
              this.style.backgroundColor = '#1e293b';
              return;
            }
            
            if (clickMarkerRef.current) {
              const latLng = clickMarkerRef.current.getLatLng();
              showRoute({
                lat: latLng.lat,
                lng: latLng.lng,
                name_mm: 'ရွေးချယ်ထားသောနေရာ'
              });
            } else {
              alert('ကျေးဇူးပြု၍ မြေပုံပေါ်တွင် နေရာတစ်ခုကို ရွေးချယ်ပါ။');
            }
          };
          
          return container;
        }
      });
      
      mapRef.current.addControl(new RouteControl());
      console.log('✅ Map created successfully');
    }

    // ✅ Cleanup
    return () => {
      console.log('🧹 Component cleanup');
      if (routingControlRef.current) {
        try {
          routingControlRef.current.off();
          if (mapRef.current) {
            mapRef.current.removeControl(routingControlRef.current);
          }
        } catch(error) {
          console.warn("Routing destroy error:", error);
        }
        routingControlRef.current = null;
      }
    };
  }, []);

  // ============================================================
  // ✅ GPS LOCATION
  // ============================================================
  useEffect(() => {
    if (!mapRef.current) return;

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          userLocationRef.current = userPos;
          
          if (userMarkerRef.current) {
            mapRef.current.removeLayer(userMarkerRef.current);
          }
          
          const userIcon = L.divIcon({
            html: '<div style="font-size:28px;text-align:center;line-height:1;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3));">📍</div>',
            className: 'user-marker',
            iconSize: [28, 28],
            iconAnchor: [14, 28]
          });
          
          userMarkerRef.current = L.marker([userPos.lat, userPos.lng], {
            icon: userIcon
          }).bindPopup('📍 သင်ရောက်ရှိနေသောနေရာ')
            .addTo(mapRef.current);
          
          mapRef.current.setView([userPos.lat, userPos.lng], 15);
          
          if (onLocationUpdate) {
            onLocationUpdate(userPos);
          }
        },
        (error) => {
          console.log('GPS location not available:', error.message);
          userLocationRef.current = { lat: 25.3833, lng: 97.4000 };
        }
      );
    }
  }, []);

  // ============================================================
  // ✅ BASEMAP CHANGE
  // ============================================================
  useEffect(() => {
    if (!mapRef.current) return;
    
    let url = basemapUrls[basemap] || basemapUrls.osm;
    let attribution = basemapAttribution[basemap] || basemapAttribution.osm;
    
    if (tileLayerRef.current) {
      mapRef.current.removeLayer(tileLayerRef.current);
    }
    
    tileLayerRef.current = L.tileLayer(url, {
      attribution: attribution
    }).addTo(mapRef.current);
    
    console.log('🗺️ Basemap changed to:', basemap);
  }, [basemap]);

  // ============================================================
  // ✅ UPDATE MARKERS
  // ============================================================
  useEffect(() => {
    if (!mapRef.current || !markerClusterRef.current) {
      console.log('⏳ Map or markerCluster not ready yet');
      return;
    }
    
    console.log('📍 Updating markers...');
    markerClusterRef.current.clearLayers();
    markersRef.current = {};

    locations.forEach(loc => {
      if (loc && loc.lat && loc.lng) {
        const lat = parseFloat(loc.lat);
        const lng = parseFloat(loc.lng);
        
        if (!isNaN(lat) && !isNaN(lng)) {
          const popupContent = createPopupContent(loc);
          
          const marker = L.marker([lat, lng])
            .bindPopup(popupContent);
          
          marker.on('popupopen', () => {
            setTimeout(() => {
              const detailBtn = document.getElementById(`detail-btn-${loc.id}`);
              if (detailBtn) {
                detailBtn.onclick = (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  console.log('📍 Detail button clicked for:', loc.name_mm);
                  
                  if (typeof onLocationSelect === 'function') {
                    onLocationSelect(loc);
                  } else {
                    console.warn('onLocationSelect is not a function');
                    alert('အသေးစိတ်ကြည့်ရန် မရသေးပါ။');
                  }
                };
              }
              
              const routeBtn = document.getElementById(`route-btn-${loc.id}`);
              if (routeBtn) {
                routeBtn.onclick = (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  console.log('🗺️ Route button clicked for:', loc.name_mm);
                  
                  if (routingControlRef.current) {
                    clearRoute();
                  }
                  showRoute(loc);
                };
              }
            }, 200);
          });
          
          if (markerClusterRef.current) {
            markerClusterRef.current.addLayer(marker);
          }
          markersRef.current[loc.id] = marker;
        }
      }
    });
    console.log('✅ Markers updated:', Object.keys(markersRef.current).length);
  }, [locations, onLocationSelect]);

  // ============================================================
  // ✅ FLOOD LAYER
  // ============================================================
  useEffect(() => {
    if (!mapRef.current) return;

    if (floodLayerRef.current) {
      mapRef.current.removeLayer(floodLayerRef.current);
      floodLayerRef.current = null;
    }

    if (waterLevel > 0 && waterLevel <= 1500) {
      const floodColor = waterLevel > 1200 ? '#dc354566' : waterLevel > 800 ? '#f59e0b66' : '#3b82f666';
      const borderColor = waterLevel > 1200 ? '#dc3545' : waterLevel > 800 ? '#f59e0b' : '#3b82f6';
      const radius = Math.min(waterLevel * 1.5, 1500);
      
      floodLayerRef.current = L.circle([25.3831, 97.3964], {
        radius: radius,
        color: borderColor,
        fillColor: floodColor,
        fillOpacity: 0.4,
        weight: 2
      }).addTo(mapRef.current);
      
      floodLayerRef.current.bindPopup(`🌊 ရေမှတ်: ${waterLevel} cm`);
    }
  }, [waterLevel]);

  // ============================================================
  // ✅ RENDER
  // ============================================================
  return (
    <>
      <div id="map" style={{ height: '100vh', width: '100%' }} />
      
      {/* ✅ No Location Label - GPS ပိတ်ထားရင် ပြမယ် */}
      {!showGPS && (
        <div style={{
          position: 'absolute',
          bottom: '30px',
          right: '30px',
          background: 'rgba(239, 68, 68, 0.9)',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: '500',
          zIndex: 1000,
          border: '1px solid #ef4444',
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
          backdropFilter: 'blur(4px)',
          pointerEvents: 'none'
        }}>
          <span style={{ marginRight: '6px' }}>📍</span> GPS ပိတ်ထားသည်
        </div>
      )}

      {/* Modal */}
      {showDetailModal && selectedLocationDetail && (
        <LocationDetailModal
          location={selectedLocationDetail}
          isOpen={showDetailModal}
          onClose={handleDetailClose}
          onRoute={handleDetailRoute}
        />
      )}
    </>
  );
});

export default MapView;